// src/pages/TrackCreation/Step1.tsx
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import shell from "../../pages/track/create/createShell.module.css";

interface Subcategory {
    name: string;
    step: string;
}

interface SidebarItem {
    title: string;
    step?: string;
    hasSubcategories: boolean;
    subcategories?: Subcategory[];
}

const HotInfoSidebar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    // Replace the segment matching "partners-*" in the current URL with the new step.
    const replaceStepInUrl = (newStep: string): string => {
        return location.pathname.replace(/partners-[^/]+/, newStep);
    };

    // Extract a value from the URL using a regex pattern.
    // For example, it matches URLs like "/create/tour/.../partners/partners-.../.../<value>"
    const extractValueFromPattern = (): string | null => {
        const regex = /^\/create\/tour\/[^/]+\/partners\/partners-[^/]+\/[^/]+\/([^/]+)$/;
        const match = location.pathname.match(regex);
        return match ? match[1] : null;
    };

    // Determine whether additional subcategories should be shown.
    const additionalSubcategories = extractValueFromPattern() !== null;

    // Sidebar structure.
    const sidebarItems: SidebarItem[] = [
        {
            title: t("hotInfoDetails"),
            hasSubcategories: true,
            subcategories: [
                { name: t("language"), step: "partners-language" },
                ...(additionalSubcategories
                    ? [
                          { name: t("coordinates"), step: "partners-coordinates" },
                          { name: t("content"), step: "partners-content" },
                          { name: t("pictures"), step: "partners-photos" },
                      ]
                    : []),
            ],
        },
    ];

    const handleBackClick = (): void => {
        const newPath = location.pathname.replace(/\/partners-[^/]+\/.+$/, "");
        navigate(newPath);
    };

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, step: string): void => {
        // Optional: add extra logic if needed.
    };

    return (
        <div className={`track-create-sidebar-navigation ${shell.navRoot}`}>
            <ul className={`track-create-sidebar-nav ${shell.navList}`}>
                {sidebarItems.map((item, index) => (
                    <div key={index} className={shell.navGroup}>
                        {!item.hasSubcategories ? (
                            <li
                                className={`sidebar-title ${shell.navTitleLink} ${
                                    item.step && location.pathname.includes(item.step) ? shell.navTitleLinkActive : ""
                                }`}
                            >
                                <Link
                                    to={replaceStepInUrl(item.step || "")}
                                    className="sidebar-link"
                                    onClick={(e) => handleLinkClick(e, item.step || "")}
                                >
                                    {item.title}
                                </Link>
                            </li>
                        ) : (
                            <>
                                <li className={`sidebar-title ${shell.navHeading}`}>{item.title}</li>
                                <ul className={`subcategory-list ${shell.navSubList}`}>
                                    {item.subcategories?.map((sub, subIndex) => (
                                        <li
                                            key={subIndex}
                                            className={`${shell.navSubItem} ${
                                                location.pathname.includes(sub.step) ? shell.navSubItemActive : ""
                                            }`}
                                        >
                                            <Link
                                                to={replaceStepInUrl(sub.step)}
                                                className="sidebar-link"
                                                onClick={(e) => handleLinkClick(e, sub.step)}
                                            >
                                                {sub.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                ))}
            </ul>
            <Button
                label={t("backToTheMap")}
                variant="outline"
                shape="pill"
                className={shell.navBackBtn}
                onClick={handleBackClick}
            />
        </div>
    );
};

export default HotInfoSidebar;
